<template>
  <div class="mx-auto my-8 text-center border-0">
    <!-- Header Text -->
    <div v-if="!hasScanned">
      <h1 class="text-4xl font-medium text-gray-900 mb-8">Run Diagnostic</h1>
      <p class="text-gray-700 text-base">
        Start diagnosing a website by entering a URL.
      </p>
      <p class="text-gray-400 mb-6 text-base">
        Sample Format: https://www.example.com
      </p>
    </div>
    <!-- Input + Button -->
    <form
      @submit.prevent="handleButtonClick"
      class="flex flex-col sm:flex-row items-center gap-4 justify-center"
    >
      <input
        v-model="websiteUrl"
        type="url"
        class="w-full sm:w-[400px] max-w-4xl md:w-3/5 px-5 py-4 text-gray-700 placeholder-gray-400 rounded-xl border border-[#3B81F6] focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
        placeholder="Enter URL or paste text here"
        :disabled="loading || hasScanned"
        @blur="normalizeInputUrl"
        required
      />
      <button
        type="button"
        @click="handleButtonClick"
        :class="[
          'flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-white transition font-semibold',
          loading || !isValidUrl(websiteUrl.trim())
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700',
        ]"
        :disabled="loading || !isValidUrl(websiteUrl.trim())"
      >
        <svg
          v-if="loading"
          class="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          ></path>
        </svg>
        <span v-else>
          {{ hasScanned ? "Diagnose Another Website" : "Diagnose" }}
        </span>
      </button>
    </form>

    <!-- Scan Complete Popup -->
    <transition name="popup-slide">
      <div
        v-if="scanStatusLink"
        class="fixed top-5 right-5 z-50 max-w-sm rounded-lg border border-green-200 bg-green-50 px-4 py-3 shadow-lg"
      >
        <div class="flex items-start gap-3">
          <CheckCircle2 class="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div class="flex-1">
            <p class="text-sm font-semibold text-green-800">
              Scan Completed, please click the URL
            </p>
            <a
              :href="scanStatusLink"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-green-700 underline hover:text-green-900 break-all"
            >
              {{ scanStatusLink }}
            </a>
          </div>
          <button
            @click="dismissScanStatus"
            class="text-green-700 hover:text-green-900 leading-none p-1"
            aria-label="Dismiss scan complete message"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Error Message -->
    <p v-if="errorMessage" class="text-red-500 mt-3 text-sm">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import axios from "axios";
import baseUrl from "../config";
import { normalizeUrl } from "../utils/urlUtils";
import { CheckCircle2, X } from "lucide-vue-next";

export default {
  name: "WebsiteInput",
  components: {
    CheckCircle2,
    X,
  },
  props: {
    modelValue: String,
    hasScanned: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "loading", "results", "reset", "error"],

  data() {
    return {
      errorMessage: "",
      loading: false,
      scanStatusLink: "",
      scanStatusTimer: null,
    };
  },

  computed: {
    websiteUrl: {
      get() {
        return this.modelValue || "";
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },

  methods: {
    normalizeInputUrl() {
      if (this.websiteUrl) {
        this.websiteUrl = normalizeUrl(this.websiteUrl, { forceHttps: false });
      }
    },

    isValidUrl(value) {
      const pattern = new RegExp(
        "^(https?:\\/\\/)?" +
          "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})" +
          "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
          "(\\?[;&a-z\\d%_.~+=-]*)?" +
          "(\\#[-a-z\\d_]*)?$",
        "i"
      );
      return !!pattern.test(value);
    },

    handleButtonClick() {
      if (this.hasScanned && !this.loading) {
        this.errorMessage = "";
        this.clearScanStatusTimer();
        this.scanStatusLink = "";
        this.$emit("error", "");
        this.$emit("reset");
        return;
      }
      this.submitUrl();
    },

    async submitUrl() {
      this.errorMessage = "";
      this.clearScanStatusTimer();
      this.scanStatusLink = "";
      this.loading = true;
      this.$emit("loading");

      const formattedUrl = this.websiteUrl.trim();
      if (!this.isValidUrl(formattedUrl)) {
        this.errorMessage = "Invalid URL format. Please enter a valid URL.";
        this.loading = false;
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        this.errorMessage = "You are not authenticated. Please log in.";
        this.loading = false;
        return;
      }

      try {
        // ✅ Direct call — no job_id or polling
        const response = await axios.post(
          `${baseUrl}/analyze-website`,
          { url: formattedUrl },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // ✅ Emit results immediately
        this.$emit("results", response.data);
        this.scanStatusLink = formattedUrl;
        // Auto-remove after 5 seconds with swipe animation
        this.clearScanStatusTimer();
        this.scanStatusTimer = setTimeout(() => {
          this.dismissScanStatus();
        }, 5000);
        this.loading = false;
      } catch (err) {
        this.errorMessage =
          err.response?.data?.error || "Error initiating scan.";
        this.$emit("error", this.errorMessage);
        this.clearScanStatusTimer();
        this.scanStatusLink = "";
        this.loading = false;
      }
    },

    clearScanStatusTimer() {
      if (this.scanStatusTimer) {
        clearTimeout(this.scanStatusTimer);
        this.scanStatusTimer = null;
      }
    },

    dismissScanStatus() {
      this.clearScanStatusTimer();
      this.scanStatusLink = "";
    },

    pollScanStatus(jobId) {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${baseUrl}/scan-status/${jobId}`);
          const result = response.data;

          if (result.status === "done") {
            clearInterval(interval);
            this.$emit("results", result.data);
            this.loading = false;
          } else if (result.status === "error") {
            clearInterval(interval);
            this.errorMessage = result.message || "Scan failed.";
            this.$emit("error", this.errorMessage);
            this.loading = false;
          }
          // else: still processing — keep polling
        } catch (error) {
          clearInterval(interval);
          this.errorMessage =
            error.response?.data?.error || "Error polling scan result.";
          this.$emit("error", this.errorMessage);
          this.loading = false;
        }
      }, 3000); // ⏱ Poll every 3 seconds
    },
  },
  beforeUnmount() {
    this.clearScanStatusTimer();
  },
};
</script>

<style scoped>
/* Popup swipe transition */
.popup-slide-enter-active,
.popup-slide-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}
.popup-slide-enter-from {
  transform: translateX(120%);
  opacity: 0;
}
.popup-slide-leave-to {
  transform: translateX(120%);
  opacity: 0;
}
</style>
